import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { apiFetch } from 'shared/apiClient';
import type { Role } from '../shared/permissions';
import {
  LS_ORG_ID,
  LS_SUPPORT_MODE,
  LS_SUPPORT_ROLE,
  LS_SUPPORT_ORG_ID,
  LS_SUPPORT_ORG_NAME,
  LS_SUPPORT_ORG_SLUG,
  LS_SUPPORT_ORG_DISPLAY_NAME,
  LS_ORIGINAL_ORG_ID,
  LS_SUPPORT_HISTORY,
} from '../shared/config/storageKeys';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SupportOrg {
  id: string;
  name: string;
  slug: string;
  display_name?: string;
  is_demo?: boolean;
  created_at: string;
}

interface SupportContextType {
  isSupportUser: boolean;
  supportMode: boolean;
  targetOrg: SupportOrg | null;
  effectiveRole: Role;
  enterSupportMode: (org: SupportOrg, role: Role) => void;
  exitSupportMode: () => void;
  switchRole: (role: Role) => void;
  searchOrgs: (query: string) => Promise<SupportOrg[]>;
  originalOrgId: string | null;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const SupportContext = createContext<SupportContextType | null>(null);

export function useSupport(): SupportContextType {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error('useSupport must be used within SupportProvider');
  return ctx;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface SupportProviderProps {
  isSupportUser: boolean;
  /** Only trust `isSupportUser` after `/api/auth/me` succeeded (avoids wiping support LS on 429 / races). */
  authMeSucceeded: boolean;
  children: React.ReactNode;
}

export function SupportProvider({
  isSupportUser,
  authMeSucceeded,
  children,
}: SupportProviderProps) {
  const [supportMode, setSupportMode] = useState(
    () => localStorage.getItem(LS_SUPPORT_MODE) === 'true'
  );
  const [effectiveRole, setEffectiveRole] = useState<Role>(() =>
    parseStoredSupportRole(localStorage.getItem(LS_SUPPORT_ROLE))
  );
  const [targetOrg, setTargetOrg] = useState<SupportOrg | null>(() => {
    if (localStorage.getItem(LS_SUPPORT_MODE) !== 'true') return null;
    return {
      id: localStorage.getItem(LS_SUPPORT_ORG_ID) || '',
      name: localStorage.getItem(LS_SUPPORT_ORG_NAME) || 'Unknown',
      slug: localStorage.getItem(LS_SUPPORT_ORG_SLUG) || '',
      display_name: localStorage.getItem(LS_SUPPORT_ORG_DISPLAY_NAME) || undefined,
      created_at: '',
    };
  });
  const [originalOrgId] = useState<string | null>(() => localStorage.getItem(LS_ORIGINAL_ORG_ID));

  // If the server says this user is NOT support but localStorage says they are,
  // clear the stale state (e.g. SUPPORT_USER_IDS was changed on the server).
  // Never clear when `/api/auth/me` failed (429, etc.) — `isSupportUser` would be wrong until retry.
  useEffect(() => {
    if (!authMeSucceeded) return;
    if (!isSupportUser && localStorage.getItem(LS_SUPPORT_MODE) === 'true') {
      clearSupportLocalStorage();
      setSupportMode(false);
      setTargetOrg(null);
    }
  }, [authMeSucceeded, isSupportUser]);

  const enterSupportMode = useCallback((org: SupportOrg, role: Role) => {
    const currentOrgId = localStorage.getItem(LS_ORG_ID);
    if (currentOrgId) {
      localStorage.setItem(LS_ORIGINAL_ORG_ID, currentOrgId);
    }

    localStorage.setItem(LS_SUPPORT_MODE, 'true');
    localStorage.setItem(LS_SUPPORT_ROLE, role);
    localStorage.setItem(LS_SUPPORT_ORG_ID, org.id);
    localStorage.setItem(LS_SUPPORT_ORG_NAME, org.name);
    localStorage.setItem(LS_SUPPORT_ORG_SLUG, org.slug);
    if (org.display_name) {
      localStorage.setItem(LS_SUPPORT_ORG_DISPLAY_NAME, org.display_name);
    } else {
      localStorage.removeItem(LS_SUPPORT_ORG_DISPLAY_NAME);
    }
    localStorage.setItem(LS_ORG_ID, org.id);

    // Track history
    appendToHistory(org, role);

    // Full reload to re-bootstrap AuthContext with the new org
    window.location.reload();
  }, []);

  const exitSupportMode = useCallback(() => {
    const savedOrgId = localStorage.getItem(LS_ORIGINAL_ORG_ID);
    clearSupportLocalStorage();

    if (savedOrgId) {
      localStorage.setItem(LS_ORG_ID, savedOrgId);
    }
    localStorage.removeItem(LS_ORIGINAL_ORG_ID);

    window.location.reload();
  }, []);

  const switchRole = useCallback((role: Role) => {
    localStorage.setItem(LS_SUPPORT_ROLE, role);
    setEffectiveRole(role);
    window.location.reload();
  }, []);

  const searchOrgs = useCallback(async (query: string): Promise<SupportOrg[]> => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', '20');
    const res = await apiFetch(`/api/support/orgs?${params}`);
    if (!res.ok) throw new Error('Failed to search organizations');
    return res.json();
  }, []);

  const value = useMemo<SupportContextType>(
    () => ({
      isSupportUser,
      supportMode,
      targetOrg,
      effectiveRole,
      enterSupportMode,
      exitSupportMode,
      switchRole,
      searchOrgs,
      originalOrgId,
    }),
    [
      isSupportUser,
      supportMode,
      targetOrg,
      effectiveRole,
      enterSupportMode,
      exitSupportMode,
      switchRole,
      searchOrgs,
      originalOrgId,
    ]
  );

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseStoredSupportRole(raw: string | null): Role {
  const r = ((raw ?? '') as string).trim().toLowerCase();
  if (r === 'owner' || r === 'administrator' || r === 'member') return r;
  return 'administrator';
}

function clearSupportLocalStorage() {
  localStorage.removeItem(LS_SUPPORT_MODE);
  localStorage.removeItem(LS_SUPPORT_ROLE);
  localStorage.removeItem(LS_SUPPORT_ORG_ID);
  localStorage.removeItem(LS_SUPPORT_ORG_NAME);
  localStorage.removeItem(LS_SUPPORT_ORG_SLUG);
  localStorage.removeItem(LS_SUPPORT_ORG_DISPLAY_NAME);
}

interface HistoryEntry {
  orgId: string;
  orgName: string;
  role: string;
  timestamp: string;
}

function appendToHistory(org: SupportOrg, role: string) {
  try {
    const raw = localStorage.getItem(LS_SUPPORT_HISTORY);
    const history: HistoryEntry[] = raw ? JSON.parse(raw) : [];
    history.unshift({
      orgId: org.id,
      orgName: org.name,
      role,
      timestamp: new Date().toISOString(),
    });
    localStorage.setItem(LS_SUPPORT_HISTORY, JSON.stringify(history.slice(0, 10)));
  } catch {
    // ignore parse errors
  }
}

export function getSupportHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(LS_SUPPORT_HISTORY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
